import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { computePageSlices } from './consentPdfSlicer';
import { fitPdfIdentification } from './consentPdfIdentification';
import { acquireConsentPdfCapture, withConsentPdfBranding } from './consentPdfBranding';
import { mountCompletedConsentDocument, documentAccent } from './completedConsentDocument';

// ─── Programmatic field value renderers ───────────────────────────────────────
// These handle the 5 new field types introduced in ConsentV2 that need
// human-readable text output in PDF exports for SubmissionDetailDialog.

/**
 * Convert a raw field value to a display-friendly string.
 * Handles: toggle_ack, toggle_detail, toggle_text, pill_select, likert_scale
 * Falls back to a basic string conversion for all other types.
 *
 * @param {string} fieldType   - frontend_type from field.metadata
 * @param {*}      value       - raw stored value
 * @param {object} [fieldDef]  - optional full field definition (for option labels)
 * @returns {string}
 */
export function renderFieldValueText(fieldType, value, fieldDef = {}) {
    if (value === null || value === undefined || value === '') return '—';

    switch (fieldType) {
        case 'toggle_ack': {
            // Boolean acknowledge toggle — true means "Acknowledged"
            if (value === true || value === 'true') return '✓ Acknowledged';
            if (value === false || value === 'false') return '✗ Not acknowledged';
            return String(value);
        }

        case 'toggle_detail': {
            // Boolean with optional free-text detail
            // Stored as { agreed: true, detail: 'some text' } or plain boolean
            if (typeof value === 'object' && value !== null) {
                const agreed = value.agreed ?? value.checked ?? value.value;
                const detail = value.detail || value.text || '';
                const base = agreed ? '✓ Yes' : '✗ No';
                return detail ? `${base}\nDetails: ${detail}` : base;
            }
            return value === true || value === 'true' ? '✓ Yes' : '✗ No';
        }

        case 'toggle_text': {
            // Boolean + required free-text only when true
            // Stored as { enabled: true, text: 'patient notes' } or plain boolean/string
            if (typeof value === 'object' && value !== null) {
                const enabled = value.enabled ?? value.checked ?? value.value;
                const text = value.text || value.detail || '';
                const base = enabled ? '✓ Yes' : '✗ No';
                return text ? `${base}\n${text}` : base;
            }
            return value === true || value === 'true' ? '✓ Yes' : String(value);
        }

        case 'pill_select': {
            // Single or multi-select from pill buttons
            // Value is a string key or array of keys; fieldDef.options maps key → label
            const options = fieldDef.options || fieldDef.metadata?.options || [];
            const getLabel = (key) => {
                const opt = options.find((o) => o.value === key || o.id === key || o === key);
                return (opt && (opt.label || opt.name || opt.value)) || key;
            };
            if (Array.isArray(value)) {
                return value.map(getLabel).join(', ') || '—';
            }
            return getLabel(String(value));
        }

        case 'likert_scale': {
            // Numeric or string rating on a scale
            // fieldDef may have { min_label, max_label, scale } for context
            const scale = fieldDef.scale || fieldDef.metadata?.scale || 5;
            const minLabel = fieldDef.min_label || fieldDef.metadata?.min_label || '';
            const maxLabel = fieldDef.max_label || fieldDef.metadata?.max_label || '';
            const numeric = Number(value);
            const ratingStr = `${numeric} / ${scale}`;
            if (minLabel || maxLabel) {
                return `${ratingStr}  (${minLabel || ''}…${maxLabel || ''})`;
            }
            return ratingStr;
        }

        default: {
            // Boolean
            if (value === true) return 'Yes';
            if (value === false) return 'No';
            // Array (multi-select checkboxes, etc.)
            if (Array.isArray(value)) return value.join(', ') || '—';
            // Object (signature, date, nested)
            if (typeof value === 'object') {
                try { return JSON.stringify(value); } catch { return '[complex value]'; }
            }
            return String(value);
        }
    }
}

const MAX_PDF_SIZE = 4.8 * 1024 * 1024; // 4.8 MB — stay safely under 5 MB backend limit
const PAGE_MARGIN_MM = 12; // margin around content on each page
const CONTENT_WIDTH_MM = 210 - PAGE_MARGIN_MM * 2; // A4 width minus margins
const PAGE_HEIGHT_MM = 297;
const CONTENT_HEIGHT_MM = PAGE_HEIGHT_MM - PAGE_MARGIN_MM * 2; // usable height per page

// Render strategy:
//   1. Always start with PNG at full scale — text and signatures stay crisp.
//   2. Only fall back to JPEG (and only the quality steps we need) when the
//      PNG result would blow past the 5 MB submit limit.
//   3. Last-resort scale-down so we *always* return *something* rather than
//      blocking the patient at submit time.

/**
 * Compute the canvas-pixel Y coordinates at which a page break is safe.
 *
 * "Safe" means: the boundary sits between two top-level visual blocks
 * (e.g. between two toggle cards, or between a section heading and the
 * first field below it). We never want to slice through the middle of a
 * card — that produced the truncated "to me. I have…" / "treatmen…"
 * artefacts in the original PDF.
 *
 * @param {HTMLElement} element      - capture root (same one passed to html2canvas)
 * @param {HTMLCanvasElement} canvas - rendered canvas (already scaled by html2canvas)
 * @returns {number[]} Sorted unique pixel offsets in the canvas's coordinate space.
 *   Always includes 0 (top) and the canvas height (bottom).
 */
export function collectBlockBoundaries(element, canvas) {
    const boundaries = new Set([0, canvas.height]);
    if (!element) return [0, canvas.height];

    const rootRect = element.getBoundingClientRect();
    const rootTop = rootRect.top;
    // html2canvas may downscale on memory pressure → compare against the
    // *effective* scale used (canvas pixels per CSS pixel), not the value
    // we requested.
    const effectiveScale = rootRect.height
        ? canvas.height / rootRect.height
        : 1;

    const nodes = element.querySelectorAll('[data-pdf-block]');
    nodes.forEach((node, index) => {
        const rect = node.getBoundingClientRect();
        // Top edge (safe break BEFORE this block)
        const top = Math.max(0, Math.round((rect.top - rootTop) * effectiveScale));
        // Bottom edge (safe break AFTER this block)
        const bottom = Math.min(
            canvas.height,
            Math.round((rect.bottom - rootTop) * effectiveScale),
        );
        // A section heading travels with the first answer, never alone at a
        // page foot. Field blocks otherwise stay whole whenever they fit.
        const headingKinds = ['section-heading', 'pricing-heading'];
        if (!headingKinds.includes(nodes[index - 1]?.getAttribute('data-pdf-block'))) boundaries.add(top);
        if (!headingKinds.includes(node.getAttribute('data-pdf-block'))) boundaries.add(bottom);
        if (element.hasAttribute('data-completed-document') && rect.height > (245 * 96 / 25.4)) {
            // Oversized clinical wording can span pages. Use actual laid-out
            // line boxes, not arbitrary pixel cuts through letters.
            const lineBoxes = [];
            const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
            let textNode = walker.nextNode();
            while (textNode) {
                if (textNode.textContent.trim()) {
                    const range = document.createRange();
                    range.selectNodeContents(textNode);
                    lineBoxes.push(...Array.from(range.getClientRects?.() || []));
                }
                textNode = walker.nextNode();
            }
            node.querySelectorAll('img, tr').forEach((child) => {
                const box = child.getBoundingClientRect();
                if (box.height <= 245 * 96 / 25.4) lineBoxes.push(box);
            });
            // Merge inline fragments (including the smaller field number)
            // into complete visual lines so a break never cuts larger glyphs.
            const rows = [];
            lineBoxes.sort((a, b) => a.top - b.top).forEach((box) => {
                const last = rows[rows.length - 1];
                if (last && box.top < last.bottom - 0.5) last.bottom = Math.max(last.bottom, box.bottom);
                else rows.push({ top: box.top, bottom: box.bottom });
            });
            rows.forEach((row) => {
                const end = Math.round((row.bottom - rootTop + 1) * effectiveScale);
                if (end > top && end < bottom) boundaries.add(end);
            });
        }
    });

    return Array.from(boundaries).sort((a, b) => a - b);
}

/**
 * Slice a canvas into A4 pages and assemble a PDF blob.
 *
 * Defers all layout decisions to ``computePageSlices`` (pure, unit-tested);
 * this function only handles the imperative DOM/PDF assembly.
 *
 * @param {HTMLCanvasElement} canvas
 * @param {Object} opts
 * @param {'PNG'|'JPEG'} opts.format    - encoding to use for the page slices
 * @param {number} [opts.quality]       - JPEG quality 0..1 (ignored for PNG)
 * @param {number[]} [opts.boundaries]  - sorted canvas-pixel Y values where it's safe
 *                                        to start a new page. Defaults to [0, height].
 * @returns {Blob}
 */
function buildPdfFromCanvas(canvas, { format, quality, boundaries, documentData, branding }) {
    // eslint-disable-next-line new-cap
    const pdf = new jsPDF('p', 'mm', 'a4');

    const imgWidthPx = canvas.width;
    const imgHeightPx = canvas.height;

    // Pixels-per-mm in the source canvas (since we scale the canvas
    // horizontally to fit CONTENT_WIDTH_MM on the page).
    const pxPerMm = imgWidthPx / CONTENT_WIDTH_MM;
    const pageHeightPx = Math.floor((documentData ? 245 : CONTENT_HEIGHT_MM) * pxPerMm);

    // See ``computePageSlices`` for the meaning of these knobs. Pulled out
    // so unit tests can reproduce the exact behaviour.
    const WIDOW_SLACK_PX = Math.floor(18 * pxPerMm);  // ≈ 5 lines of body text
    const MAX_OVERFLOW_PX = Math.floor(8 * pxPerMm);  // ≈ ½ a text line

    const slices = computePageSlices(imgHeightPx, pageHeightPx, boundaries, {
        widowSlackPx: WIDOW_SLACK_PX,
        maxOverflowPx: documentData ? 0 : MAX_OVERFLOW_PX,
    });

    slices.forEach(([sliceStart, sliceEnd], pageIndex) => {
        const sliceHeight = sliceEnd - sliceStart;
        if (pageIndex > 0) pdf.addPage();

        // Create a per-page canvas slice.
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = imgWidthPx;
        sliceCanvas.height = sliceHeight;
        const ctx = sliceCanvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, imgWidthPx, sliceHeight);
        ctx.drawImage(
            canvas,
            0, sliceStart, imgWidthPx, sliceHeight,
            0, 0, imgWidthPx, sliceHeight
        );

        const mimeType = format === 'PNG' ? 'image/png' : 'image/jpeg';
        const sliceData = format === 'PNG'
            ? sliceCanvas.toDataURL(mimeType)
            : sliceCanvas.toDataURL(mimeType, quality);
        const sliceHeightMm = sliceHeight / pxPerMm;

        pdf.addImage(
            sliceData, format,
            PAGE_MARGIN_MM, documentData && pageIndex > 0 ? 24 : PAGE_MARGIN_MM,
            CONTENT_WIDTH_MM, sliceHeightMm
        );
        if (documentData) {
            const practice = branding || documentData.form?.practice_branding || {};
            const accent = documentAccent(practice);
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(9);
            pdf.setTextColor('#637381');
            if (pageIndex > 0) {
                pdf.text(fitPdfIdentification(pdf, practice.practice_name, 82), PAGE_MARGIN_MM, 10);
                pdf.text(fitPdfIdentification(pdf, `${documentData.form?.name || ''} (continued)`, 92), 198, 10, { align: 'right' });
                pdf.setDrawColor(accent);
                pdf.setLineWidth(0.25);
                pdf.line(PAGE_MARGIN_MM, 20, 198, 20);
            }
            pdf.setFontSize(10);
            pdf.setDrawColor('#DFE3E8');
            pdf.setLineWidth(0.2);
            pdf.line(PAGE_MARGIN_MM, 276, 198, 276);
            pdf.text(fitPdfIdentification(pdf, practice.practice_name, 140, 1)[0] || '', PAGE_MARGIN_MM, 282);
            pdf.text(`Page ${pageIndex + 1} of ${slices.length}`, 198, 282, { align: 'right' });
            pdf.text('Confidential', PAGE_MARGIN_MM, 288);
            const reference = documentData.reference || (documentData.form?.id ? `Form ${documentData.form.id}` : '');
            pdf.text(fitPdfIdentification(pdf, reference, 140, 1)[0] || '', 198, 288, { align: 'right' });
        }
    });

    return pdf.output('blob');
}

/**
 * Wait for any async render work that affects the snapshot:
 *
 *   1. Web fonts — html2canvas reads the *current* font metrics. If a custom
 *      face (Inter, brand serif, …) hasn't finished loading when capture
 *      starts, characters get rendered in the fallback font and individual
 *      glyphs end up mis-positioned ("letters distorted" — exact symptom
 *      reported on 2026-05-29).
 *   2. Images — practice logos, the patient avatar, and any <img> in the
 *      brand header have to be decoded; an undecoded <img> rasterises as
 *      a broken-image glyph.
 *
 * Both waits are bounded by `WAIT_BUDGET_MS` so a stuck font/CDN can't
 * stall submission indefinitely.
 */
const WAIT_BUDGET_MS = 1500;

function withTimeout(promise, ms, label) {
    return Promise.race([
        promise,
        new Promise((resolve) => setTimeout(() => {
            // eslint-disable-next-line no-console
            if (typeof window !== 'undefined' && window.console) console.debug(`[consentPdfExport] ${label} wait timed out at ${ms}ms — proceeding`);
            resolve(null);
        }, ms)),
    ]);
}

async function waitForRenderReady(element) {
    // Web fonts.
    if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) {
        await withTimeout(document.fonts.ready, WAIT_BUDGET_MS, 'fonts.ready');
    }

    // Images. ``HTMLImageElement.decode()`` resolves only once the bitmap is
    // available for paint (covers CORS-fetched logos that complete() may lie
    // about). Falls back to a load-event promise for browsers without decode().
    if (element && typeof element.querySelectorAll === 'function') {
        const imgs = Array.from(element.querySelectorAll('img'));
        const decodes = imgs.map((img) => {
            if (img.complete && img.naturalWidth > 0 && typeof img.decode === 'function') {
                return img.decode().catch(() => null);
            }
            if (typeof img.decode === 'function') {
                return img.decode().catch(() => null);
            }
            return new Promise((resolve) => {
                if (img.complete) return resolve(null);
                img.addEventListener('load', () => resolve(null), { once: true });
                img.addEventListener('error', () => resolve(null), { once: true });
                return null;
            });
        });
        if (decodes.length) {
            await withTimeout(Promise.all(decodes), WAIT_BUDGET_MS, 'image decode');
        }
    }

    // A missing or CORS-blocked practice logo must not leave a broken-image
    // glyph. Name/address stay in the header, including after decode timeout.
    element?.querySelectorAll?.('[data-pdf-practice-logo]').forEach((img) => {
        if (!img.complete || !img.naturalWidth) img.style.display = 'none';
    });

    // Give the browser one more frame to flush any layout that the font /
    // image promises just triggered.
    await new Promise((resolve) => {
        if (typeof requestAnimationFrame === 'function') {
            requestAnimationFrame(() => resolve(null));
        } else {
            setTimeout(resolve, 16);
        }
    });
}

/**
 * Capture a DOM element with html2canvas at the device's effective DPR so
 * crisp text and signature strokes are preserved. Caps at 3× to keep memory
 * bounded on phones with very high DPRs.
 *
 * Returns an object so callers can both inspect the canvas and observe
 * timing / fallback outcomes (used by the audit-log breadcrumb).
 */
async function captureCanvas(element, scale) {
    await waitForRenderReady(element);

    const startedAt = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();

    const html2canvasPromise = html2canvas(element, {
        scale,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        allowTaint: false,
        ignoreElements: (el) => el.tagName === 'SCRIPT' || el.tagName === 'NOSCRIPT',
    });
    const timeoutPromise = new Promise((resolve) => setTimeout(() => {
        // eslint-disable-next-line no-console
        console.warn('[consentPdfExport] html2canvas timed out after 8s — returning null');
        resolve(null);
    }, 8000));
    const canvas = await Promise.race([html2canvasPromise, timeoutPromise]);

    const elapsedMs = ((typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now()) - startedAt;
    return { canvas, elapsedMs };
}

function preferredScale() {
    const dpr = typeof window !== 'undefined' && window.devicePixelRatio
        ? window.devicePixelRatio
        : 2;
    return Math.min(Math.max(dpr, 2), 3); // 2× minimum, cap at 3×
}

// ─── Practice details block ──────────────────────────────────────────────────
// Mirrors the backend fallback PDF (apps/practice/pdf_generator.py
// ::_render_practice_details_section). We mount a temporary DOM block at
// the bottom of the captured element so html2canvas picks it up as part
// of the form image, then remove it after capture. This keeps the
// existing canvas-slice → A4-pages pipeline unchanged.
//
// Rules from the spec:
//   * Render ALL entries regardless of `visible_to_patient` (BE-4 parity —
//     audit needs everything the practice set, in one place).
//   * Omit the section entirely when input is empty/undefined.
//   * Each row: bold label + ": " + display value.

const HEX_ESCAPE_MAP = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
};

function escapeHtml(value) {
    if (value === null || value === undefined) return '';
    return String(value).replace(/[&<>"']/g, (ch) => HEX_ESCAPE_MAP[ch]);
}

function humaniseKey(key) {
    if (!key) return '(unlabelled)';
    // Drop "system:" / "field:" / "practice_user:" prefixes, then split on
    // separators and title-case. Mirrors _label_for_key() in the backend
    // PDF generator so unlabeled historical entries still look sensible.
    const trimmed = String(key).replace(/^[a-z_]+:/i, '');
    const words = trimmed.split(/[_\s-]+/).filter(Boolean);
    if (!words.length) return '(unlabelled)';
    return words
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ');
}

/**
 * Build the "Practice details" DOM block to append to the form snapshot
 * before html2canvas capture. Returns null when there are no entries.
 *
 * @param {Object} practiceFieldValues - Shape: { key: { label, display, ... } }
 * @returns {HTMLElement|null}
 */
function buildPracticeDetailsNode(practiceFieldValues) {
    if (!practiceFieldValues || typeof practiceFieldValues !== 'object') return null;

    const entries = Object.entries(practiceFieldValues).filter(([, entry]) => entry && typeof entry === 'object');
    if (entries.length === 0) return null;

    const root = document.createElement('div');
    root.setAttribute('data-pdf-practice-details', 'true');
    // Mark as a single page-break-safe block so the smart slicer keeps
    // the heading and its rows together.
    root.setAttribute('data-pdf-block', 'practice-details');
    root.style.marginTop = '24px';
    root.style.paddingTop = '16px';
    root.style.borderTop = '1px solid #DFE3E8';
    root.style.fontFamily = 'inherit';
    root.style.color = '#212B36';
    root.style.pageBreakInside = 'avoid';

    const heading = document.createElement('div');
    heading.textContent = 'Practice details';
    heading.style.fontWeight = '700';
    heading.style.fontSize = '16px';
    heading.style.marginBottom = '12px';
    heading.style.color = '#212B36';
    root.appendChild(heading);

    const list = document.createElement('div');
    list.style.display = 'flex';
    list.style.flexDirection = 'column';
    list.style.gap = '6px';

    entries.forEach(([key, entry]) => {
        const rawLabel = (entry.label && String(entry.label).trim())
            || humaniseKey(key);
        const rawDisplay = (entry.display !== undefined && entry.display !== null && String(entry.display).trim())
            ? String(entry.display).trim()
            : '—';

        const row = document.createElement('div');
        row.style.fontSize = '13px';
        row.style.lineHeight = '1.5';
        row.style.color = '#212B36';
        // Inline <strong> + value matches the simple "label: display" layout
        // used for the patient answers section, so the new block sits
        // visually next to the rest of the form.
        row.innerHTML = `<strong style="font-weight:600;color:#212B36;">${escapeHtml(rawLabel)}:</strong> ${escapeHtml(rawDisplay)}`;
        list.appendChild(row);
    });

    root.appendChild(list);
    return root;
}

/**
 * Append the Practice details block to the capture target if any entries
 * exist. Returns a cleanup function that removes the node again (always
 * safe to call — no-op when nothing was inserted). The caller owns the
 * lifecycle so we don't leak DOM nodes on the patient-facing flow.
 */
function withPracticeDetails(element, practiceFieldValues) {
    const node = buildPracticeDetailsNode(practiceFieldValues);
    if (!node || !element) {
        return () => {};
    }
    element.appendChild(node);
    return () => {
        if (node.parentNode === element) {
            element.removeChild(node);
        }
    };
}

/**
 * Render a consent form DOM element into a PDF Blob (max ~5 MB).
 *
 * Strategy: PNG at high scale → PNG at fallback scale → JPEG quality steps
 * → low-scale fallback. Only drops below PNG when the file would exceed the
 * backend's 5 MB cap.
 *
 * @param {HTMLElement} element - The DOM node containing the rendered form
 * @param {Object} [practiceFieldValues] - Optional `practice_field_values`
 *   payload from the ConsentRequest. When non-empty, a "Practice details"
 *   section is appended to the PDF after the patient answers and signature
 *   block. Shape: `{ key: { label, display, value, source, visible_to_patient } }`.
 *   All entries are rendered (BE-4 parity), regardless of `visible_to_patient`.
 * @param {Function} [onAuditEvent] - Optional `(eventType, payload)` callback
 *   the caller wires to `logAuditEvent`. Emitted as `PDF_CAPTURE_SLOW`
 *   (capture exceeded 5s), `PDF_CAPTURE_FAILED` (html2canvas race timed out
 *   for a stage but a fallback may still succeed), or `PDF_CAPTURE_NULL`
 *   (all stages failed — submit will proceed without a client-rendered PDF).
 * @returns {Promise<Blob|null>} The PDF as a Blob, or null if element is missing
 */
export async function generateConsentPdfBlob(element, practiceFieldValues, onAuditEvent, practiceBranding, documentData) {
    if (!element) return null;

    const releaseCapture = await acquireConsentPdfCapture(element);
    let removePracticeDetails = () => {};
    let removeBranding = () => {};
    let removeDocument = () => {};
    let captureRoot = element;
    const audit = typeof onAuditEvent === 'function' ? onAuditEvent : () => {};
    const SLOW_CAPTURE_MS = 5000;

    function handleCaptureResult(stage, scale, result) {
        const ms = Math.round(result.elapsedMs || 0);
        if (!result.canvas) {
            // Bounded race in captureCanvas returned null — html2canvas
            // hung past 8s. Surface so prod observability sees it; the
            // FE will still attempt the next fallback below.
            audit('PDF_CAPTURE_FAILED', { stage, scale, elapsed_ms: ms });
            return false;
        }
        if (ms >= SLOW_CAPTURE_MS) {
            audit('PDF_CAPTURE_SLOW', { stage, scale, elapsed_ms: ms });
        }
        return true;
    }

    try {
        if (documentData?.form?.sections) {
            const mounted = mountCompletedConsentDocument(documentData, practiceBranding, practiceFieldValues);
            captureRoot = mounted.element;
            removeDocument = mounted.cleanup;
        } else {
            removePracticeDetails = withPracticeDetails(element, practiceFieldValues);
            removeBranding = withConsentPdfBranding(element, practiceBranding);
        }
        const highScale = preferredScale();
        const fallbackScale = Math.max(highScale - 1, 1.5);

        // 1. PNG at high scale — best quality.
        let result = await captureCanvas(captureRoot, highScale);
        if (!handleCaptureResult('png-high', highScale, result)) {
            // Hard fail at the highest-quality stage — try the lower scale
            // path next (it may succeed on a slower device).
        } else {
            let { canvas } = result;
            let boundaries = collectBlockBoundaries(captureRoot, canvas);
            let blob = buildPdfFromCanvas(canvas, { format: 'PNG', boundaries, documentData, branding: practiceBranding });
            if (blob.size <= MAX_PDF_SIZE) return blob;

            // 2. PNG at a lower scale before sacrificing colour fidelity.
            if (fallbackScale < highScale) {
                result = await captureCanvas(captureRoot, fallbackScale);
                if (handleCaptureResult('png-fallback', fallbackScale, result)) {
                    ({ canvas } = result);
                    boundaries = collectBlockBoundaries(captureRoot, canvas);
                    blob = buildPdfFromCanvas(canvas, { format: 'PNG', boundaries, documentData, branding: practiceBranding });
                    if (blob.size <= MAX_PDF_SIZE) return blob;
                }
            }

            // 3. JPEG quality steps at the fallback scale — text starts to soften
            //    here but layout stays accurate.
            const qualityLevels = [0.85, 0.7, 0.55, 0.4];
            for (let qi = 0; qi < qualityLevels.length; qi += 1) {
                blob = buildPdfFromCanvas(canvas, { format: 'JPEG', quality: qualityLevels[qi], boundaries, documentData, branding: practiceBranding });
                if (blob.size <= MAX_PDF_SIZE) return blob;
            }
        }

        // 4. Last resort — scale 1, low JPEG quality. Always returns *something*
        //    rather than blocking the patient at submit.
        const lastResult = await captureCanvas(captureRoot, 1);
        handleCaptureResult('jpeg-last-resort', 1, lastResult);
        if (!lastResult.canvas) {
            audit('PDF_CAPTURE_NULL', { stage: 'final' });
            return null;
        }
        const lastBoundaries = collectBlockBoundaries(captureRoot, lastResult.canvas);
        return buildPdfFromCanvas(lastResult.canvas, { format: 'JPEG', quality: 0.3, boundaries: lastBoundaries, documentData, branding: practiceBranding });
    } finally {
        // Always clean up — even if html2canvas threw — so we don't leave
        // the "Practice details" block hanging in the patient-visible DOM.
        try { removeDocument(); } finally {
        try { removeBranding(); } finally {
            try { removePracticeDetails(); } finally { releaseCapture(); }
        }
        }
    }
}

/**
 * Renders and downloads a PDF directly. Used by the admin "Download PDF"
 * button on SubmissionDetailDialog (no upload size constraint, so we can
 * stay on PNG end-to-end and keep page margins).
 *
 * @param {HTMLElement} element - The DOM node containing the rendered form
 * @param {string} fileName - The output file name (without .pdf extension)
 * @param {Object} [practiceFieldValues] - Optional `practice_field_values`
 *   payload from the originating ConsentRequest. See `generateConsentPdfBlob`
 *   for shape + rendering rules.
 * @returns {Promise<void>}
 */
export async function exportConsentToPdf(element, fileName, practiceFieldValues, practiceBranding, documentData) {
    if (!element) return;
    const resolvedFileName = fileName || 'consent-form';

    const releaseCapture = await acquireConsentPdfCapture(element);
    let removePracticeDetails = () => {};
    let removeBranding = () => {};
    let removeDocument = () => {};
    let captureRoot = element;

    let blob;
    try {
        if (documentData?.form?.sections) {
            const mounted = mountCompletedConsentDocument(documentData, practiceBranding, practiceFieldValues);
            captureRoot = mounted.element;
            removeDocument = mounted.cleanup;
        } else {
            removePracticeDetails = withPracticeDetails(element, practiceFieldValues);
            removeBranding = withConsentPdfBranding(element, practiceBranding);
        }
        const { canvas } = await captureCanvas(captureRoot, preferredScale());
        if (!canvas) return;
        const boundaries = collectBlockBoundaries(captureRoot, canvas);
        blob = buildPdfFromCanvas(canvas, { format: 'PNG', boundaries, documentData, branding: practiceBranding });
    } finally {
        try { removeDocument(); } finally {
        try { removeBranding(); } finally {
            try { removePracticeDetails(); } finally { releaseCapture(); }
        }
        }
    }

    // Trigger download via blob URL so the file name is honoured cross-browser.
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${resolvedFileName}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}
